import React, { useCallback, useEffect, useState } from 'react'
import { makeStyles } from '@material-ui/core'
import type { Theme } from '@material-ui/core/styles'
import type { StyleClasses } from '@paintscout/ui'
import type { Contact } from '@paintscout/api'
import AddIcon from '@material-ui/icons/Add'
import {
  Button,
  ActionButton,
  ConfirmationDialog,
  Grid,
  Paper,
  Skeleton,
  Typography,
  useDialogs,
  TableRow,
  useClientOptions,
  useUser,
  useUserPermissions,
  TablePagination
} from '@paintscout/ui'
import { ContactDialog } from 'src/components/dialogs'
import { useContactMutations, useContactSearch } from 'shared/hooks'
import { useSnackbar } from 'notistack'
import { Sentry } from 'src/util/appSentry'
import { goToContact } from 'src/util/routing'
import { useHistory, useLocation } from 'react-router-dom'
import { formatName, formatPhoneNumber, isUserAssigned, isUserOwner } from '@paintscout/util/builder'
import ContactSearchDialogNew from 'src/components/dialogs/ContactSearchDialog/ContactSearchDialogNew'

const useStyles = makeStyles<Theme, ContactContainerProps>(
  (theme) => ({
    root: {},
    contactContainer: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-start',
      [theme.breakpoints.down('sm')]: {
        border: 'none'
      }
    },
    contactHeader: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      width: '100%',
      alignItems: 'center',
      paddingTop: theme.spacing(1),
      paddingLeft: theme.spacing(2),
      [theme.breakpoints.down('sm')]: {
        paddingTop: 0
      }
    },
    addButton: {
      '@media print': {
        display: 'none'
      }
    },
    actionButton: {
      '@media print': {
        display: 'none'
      }
    },
    noContactBody: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(4),
      [theme.breakpoints.between('sm', 'md')]: {
        minHeight: 100,
        paddingTop: 0,
        paddingBottom: 0
      },
      [theme.breakpoints.down('sm')]: {
        minHeight: 100,
        paddingTop: 0,
        paddingBottom: 0
      }
    },
    contactRowRoot: {
      width: '100%',
      '&:last-child': {
        borderBottom: 'none'
      }
    },
    contactRow: {
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'center',
      padding: theme.spacing(2),
      paddingRight: theme.spacing(1),
      '&:hover': {
        cursor: 'pointer',
        backgroundColor: theme.paintscout.palette.brand.Neutral.P96
      },
      '&:not(:last-child)': {
        borderBottom: `2px solid ${theme.paintscout.palette.brand.Neutral.P90}`
      }
    },

    loadingBody: {
      padding: theme.spacing(1)
    }
  }),
  { name: 'CompanyContactContainer' }
)

export interface ContactContainerProps {
  classes?: StyleClasses<typeof useStyles>
  companyId: string
  companyName?: string
  hideHeader?: boolean
  canEdit?: boolean
  onContactChange?: () => void
}

function CompanyContactContainer(props: ContactContainerProps) {
  const location = useLocation<{ page?: number; bookmark?: string; sort?: number }>()
  const classes = useStyles(props)
  const { enqueueSnackbar } = useSnackbar()
  const { user, trialExpired } = useUser()
  const { companyName, onContactChange, companyId, canEdit } = props
  const { openDialog, dismissDialog, dismissAllDialogs } = useDialogs()
  const { saveContact } = useContactMutations()
  const { options } = useClientOptions()
  const query = `companyDocId:"${companyId}"`
  const history = useHistory()
  const { permissionToEditAssignedContacts, permissionToEditAllContacts } = useUserPermissions()
  const [page, setPage] = useState(location.state?.page ?? 1)
  const [bookmark, setBookmark] = useState(page === 1 ? undefined : location.state?.bookmark ?? undefined)
  const [prevBookmark, setPrevBookmark] = useState(undefined)
  const [doublePrevBookmark, setDoublePrevBookmark] = useState(undefined)
  const limit = 5
  const [search, { loading, data, error, fetchMore, updateQuery }] = useContactSearch({
    // if we're on page 1 we'll also refetch to get latest results
    fetchPolicy: page === 1 ? 'cache-and-network' : 'cache-first',
    limit,
    prevBookmark: bookmark,
    prevSort: ['first_name'],
    onCompleted: (newData) => {
      console.log('completing', { bookmark, prevBookmark, doublePrevBookmark })
      if (prevBookmark) {
        setDoublePrevBookmark(prevBookmark)
      }
      if (bookmark) {
        setPrevBookmark(bookmark)
      }
      setBookmark(newData?.searchContacts?.bookmark)
    }
  })
  const [total_rows, setTotalRows] = useState(0)
  const [contacts, setContacts] = useState<Contact[]>([])
  const refreshContacts = useCallback(() => {
    setPage(1)
    setBookmark(undefined)
    setPrevBookmark(undefined)
    setDoublePrevBookmark(undefined)
    search({ query, sort: ['first_name'], limit })
  }, [limit, query, search])
  useEffect(() => {
    if (data?.searchContacts) {
      setTotalRows(data.searchContacts.total_rows)
      setContacts(data?.searchContacts?.contacts ?? [])
    }
  }, [data?.searchContacts])
  const pages = total_rows < limit ? 1 : Math.min(Math.ceil(total_rows / limit), 20)
  const isFirstPage = page <= 1
  const isLastPage = page >= pages || !pages || pages === 1
  // show error toast
  useEffect(() => {
    if (error) {
      Sentry.captureException(error)
      enqueueSnackbar(`Unable to search Contacts`, { variant: 'error' })
    }
  }, [error])

  useEffect(() => {
    if (page || bookmark) {
      history.replace({
        ...history.location,
        state: {
          bookmark,
          page: page,
          sort: 'first_name'
        }
      })
    }
  }, [page, bookmark])

  useEffect(() => {
    setPage(1)
    search({ query, sort: ['first_name'], limit })
  }, [])

  const handleEditContact = (contactId: string) => {
    openDialog(ContactDialog, {
      contactId,
      onConfirm: async (updatedContact: Contact) => {
        try {
          if (updateQuery) {
            updateQuery((prev) => {
              return {
                ...prev,
                searchContacts: {
                  ...prev.searchContacts,
                  contacts: prev.searchContacts.contacts.map((contact) =>
                    contact._id === contactId ? updatedContact : contact
                  )
                }
              }
            })
          }
          setContacts((contacts) => contacts.map((contact) => (contact._id === contactId ? updatedContact : contact)))
        } catch (error) {
          console.log(error)
        } finally {
          dismissDialog()
        }
      },
      onCancel: dismissDialog
    })
  }

  const handleRemoveAssociation = useCallback(
    async (contactId: string) => {
      const contact = contacts.find((contact) => contact._id === contactId)
      openDialog(ConfirmationDialog, {
        message: <span>This will remove the contact from the company. You can add it back later if needed.</span>,
        onConfirm: async () => {
          try {
            const updatedContact = {
              ...contact,
              company: null,
              companyDocId: null
            }
            await saveContact({
              contact: updatedContact,
              refetchQueries: ['searchActivities', 'searchContacts', 'searchContactsWithIntegration']
            })
            enqueueSnackbar('Contact successfully removed', { variant: 'success' })
            if (updateQuery) {
              updateQuery((prev) => {
                return {
                  searchContacts: {
                    ...prev.searchContacts,
                    contacts: prev.searchContacts.contacts.filter((c) => c._id !== contact._id)
                  }
                }
              })
            }
            setContacts((contacts) => contacts.filter((c) => c._id !== contact._id))
            setTotalRows((total) => total - 1)
            refreshContacts()
            if (onContactChange) {
              onContactChange()
            }
            dismissAllDialogs()
          } catch (error) {
            Sentry.captureException(error, { tags: { contactId: contact._id } })
            enqueueSnackbar('Unable to remove Contact', { variant: 'error' })
          }
        },
        onCancel: dismissDialog
      })
    },
    [
      contacts,
      dismissAllDialogs,
      dismissDialog,
      enqueueSnackbar,
      onContactChange,
      refreshContacts,
      saveContact,
      updateQuery
    ]
  )

  const handleCreateAssociation = useCallback(async () => {
    openDialog(ContactSearchDialogNew, {
      filters: [
        {
          id: 'companyDocId',
          type: 'exists',
          not: true
        }
      ],
      blockIntegration: true,
      onCreateNew: (queryString: string) => {
        openDialog(ContactDialog, {
          hideCompany: true,
          defaultCompany: {
            id: companyId,
            name: companyName ?? ''
          },
          queryString,
          onConfirm: async (contact: Contact) => {
            enqueueSnackbar('Contact successfully linked', { variant: 'success' })
            try {
              if (updateQuery) {
                updateQuery((prev) => {
                  return {
                    searchContacts: {
                      ...prev.searchContacts,
                      contacts: [...prev.searchContacts.contacts, contact]
                    }
                  }
                })
              }
              setContacts((contacts) => [...contacts, contact])
              setTotalRows((total) => total + 1)
              refreshContacts()
            } catch (error) {
              console.log({ error })
            }
            if (onContactChange) {
              onContactChange()
            }
            dismissAllDialogs()
          },
          onCancel: dismissAllDialogs
        })
      },
      onConfirm: async (newContact: Contact, _currentDealId?: number | string, _syncProvider?: string) => {
        const updatedContact = {
          ...newContact,
          company: companyName,
          companyDocId: companyId
        }
        await saveContact({
          contact: updatedContact
        })
        enqueueSnackbar('Contact successfully linked', { variant: 'success' })
        if (updateQuery) {
          updateQuery((prev) => {
            return {
              searchContacts: {
                ...prev.searchContacts,
                contacts: [...prev.searchContacts.contacts, updatedContact]
              }
            }
          })
        }
        setContacts((contacts) => [...contacts, updatedContact])
        setTotalRows((total) => total + 1)
        refreshContacts()
        if (onContactChange) {
          onContactChange()
        }
        dismissAllDialogs()
      },
      onCancel: dismissDialog
    })
  }, [
    companyId,
    companyName,
    dismissAllDialogs,
    dismissDialog,
    enqueueSnackbar,
    onContactChange,
    openDialog,
    refreshContacts,
    saveContact,
    updateQuery
  ])

  function handleChangePage(event, nextPage) {
    setPage(nextPage)
    const nextPageFirstIndex = (nextPage - 1) * limit
    const dataExists = !!contacts[nextPageFirstIndex]
    if (!dataExists) {
      fetchMore()
    }
  }
  const rowsForPage = contacts?.slice((page - 1) * limit, (page - 1) * limit + limit) || []

  return (
    <Paper className={classes.contactContainer}>
      <div className={classes.contactHeader}>
        {!props?.hideHeader ? (
          <>
            <Typography variant={'h3'}>Contacts</Typography>
            {canEdit && (
              <Button
                className={classes.addButton}
                variant="hoverUnderline"
                color="primary"
                onClick={() => handleCreateAssociation()}
                startIcon={<AddIcon />}
              >
                Add
              </Button>
            )}
          </>
        ) : null}
      </div>
      {loading && (
        <div className={classes.loadingBody}>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Skeleton width="100%" height="50px" />
            </Grid>
            <Grid item xs={12}>
              <Skeleton width="100%" height="50px" />
            </Grid>
          </Grid>
        </div>
      )}
      {rowsForPage.length > 0 && !loading && (
        <>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {rowsForPage.map((contact) => {
              const subtitle = `${contact.phone_number ? formatPhoneNumber({ options, phoneNumber: contact.phone_number, extension: contact.phone_ext }) + ' · ' : ''}${contact.email || ''}`
              const isAssigned = isUserAssigned(contact, user.user_id)
              const isOwner = isUserOwner(contact, user.user_id)
              const canEdit =
                (isOwner || (isAssigned && permissionToEditAssignedContacts) || permissionToEditAllContacts) &&
                !trialExpired
              return (
                <TableRow
                  className={classes.contactRowRoot}
                  key={contact._id}
                  data-testid={`contact-${contact._id}`}
                  onClick={() => goToContact(contact._id)}
                >
                  <Grid container className={classes.contactRow}>
                    <Grid item xs>
                      <Typography>{formatName(contact)}</Typography>
                      <Typography variant={'subtitle2'}>{subtitle}</Typography>
                    </Grid>
                    <Grid item style={{ marginLeft: '8px' }}>
                      {canEdit && (
                        <ActionButton
                          className={classes.actionButton}
                          actions={[
                            {
                              label: 'Edit',
                              key: 'edit'
                            },
                            {
                              label: 'Remove Association',
                              key: 'remove-association'
                            }
                          ]}
                          onActionClick={(_e, action) => {
                            switch (action) {
                              case 'edit':
                                handleEditContact(contact._id)
                                break
                              case 'remove-association':
                                handleRemoveAssociation(contact._id)
                                break
                            }
                          }}
                        />
                      )}
                    </Grid>
                  </Grid>
                </TableRow>
              )
            })}
          </div>

          {pages > 1 && (
            <TablePagination
              currentPage={page}
              pages={pages}
              disablePrevPage={loading || isFirstPage}
              disableNextPage={loading || isLastPage}
              disableFirstPage={loading || isFirstPage}
              disableLastPage={loading || isLastPage}
              onChangePage={handleChangePage}
            />
          )}
        </>
      )}
      {rowsForPage.length === 0 && !loading && (
        <div className={classes.noContactBody}>
          <Typography variant={'subtitle1'}>No contacts found.</Typography>
        </div>
      )}
    </Paper>
  )
}

export default CompanyContactContainer
